import { makeAutoObservable, toJS } from 'mobx'
import {
  ViewColumnFragment,
  ViewColumnType,
  ViewCustomColumnTemplateBase,
  ViewFilterFragment,
  ViewFragment,
  ViewSortFragment,
  ViewType,
} from 'types/graphql'

import { ColumnManager } from 'src/models/TableViews/ColumnManager'
import { SortManager } from 'src/models/TableViews/SortManager'
import { TableViewColumnTemplateStore } from 'src/models/TableViews/TableViewColumnTemplateStore'
import { TableViewFilter } from 'src/models/TableViews/TableViewFilter'

import { FilterManager } from './FilterManager'

export class TableView {
  public readonly id: number
  public readonly columnManager: ColumnManager
  public readonly customizableColumnTemplates: ViewCustomColumnTemplateBase[]
  public readonly filterManager: FilterManager
  public readonly sortManager: SortManager
  public readonly viewType: ViewType

  public totalCount: number
  public databaseTotalCount: number
  public databaseDisplayIndex: number
  public displayIndex: number
  public name: string

  private _entityLayer: ViewFragment['entityLayer']
  private _columnTemplateStore: TableViewColumnTemplateStore
  private _customizableColumnTemplatesByType: Map<ViewColumnType, ViewCustomColumnTemplateBase>
  private _updatedAt: Date

  constructor(args: {
    columns: ViewColumnFragment[]
    columnTemplateStore: TableViewColumnTemplateStore
    customizableColumnTemplates: ViewCustomColumnTemplateBase[]
    displayIndex: number
    id: number
    filters: ViewFilterFragment[]
    name: string
    sorts: ViewSortFragment[]
    entityLayer?: ViewFragment['entityLayer']
    totalCount: number
    updatedAt: Date
    viewType: ViewType
  }) {
    this._entityLayer = args.entityLayer
    this._columnTemplateStore = args.columnTemplateStore
    this.columnManager = new ColumnManager({
      columns: args.columns,
      columnTemplateStore: args.columnTemplateStore,
    })
    this.customizableColumnTemplates = args.customizableColumnTemplates
    this._customizableColumnTemplatesByType = new Map(
      args.customizableColumnTemplates.map((ct) => [ct.columnType, ct])
    )
    this.displayIndex = args.displayIndex
    this.databaseDisplayIndex = args.displayIndex
    this.id = args.id
    this.filterManager = new FilterManager({
      columnTemplateStore: args.columnTemplateStore,
      filters: args.filters,
    })
    this.name = args.name
    this.sortManager = new SortManager()
    this.sortManager.initializeSorts(args.sorts)
    this.totalCount = args.totalCount
    this.databaseTotalCount = args.totalCount
    this._updatedAt = args.updatedAt
    this.viewType = args.viewType
    makeAutoObservable(this)
  }

  static fromProtocol(
    protocol: ViewFragment,
    columnTemplateStore: TableViewColumnTemplateStore,
    options?: { hideBaseFilters?: boolean }
  ): TableView {
    return new TableView({
      columns: protocol.columns ?? [],
      columnTemplateStore,
      customizableColumnTemplates: protocol.customizableColumnTemplates ?? [],
      displayIndex: protocol.displayIndex,
      filters: options?.hideBaseFilters ? [] : (protocol.filters ?? []),
      entityLayer: protocol.entityLayer,
      id: protocol.id,
      name: protocol.name,
      sorts: protocol.sorts ?? [],
      totalCount: 0,
      updatedAt: new Date(protocol.updatedAt),
      viewType: protocol.viewType,
    })
  }

  public clone(): TableView {
    return new TableView({
      columns: this.columnManager.columns,
      columnTemplateStore: this._columnTemplateStore,
      customizableColumnTemplates: this.customizableColumnTemplates,
      entityLayer: this._entityLayer,
      id: this.id,
      displayIndex: this.displayIndex,
      name: this.name,
      filters: this.filterManager.populatedFilters.map((f) => f.protocol),
      sorts: this.sortManager.sorts,
      totalCount: this.databaseTotalCount,
      updatedAt: this._updatedAt,
      viewType: this.viewType,
    })
  }

  public reset() {
    this.filterManager.reset()
    this.sortManager.reset()
    this.totalCount = this.databaseTotalCount
  }

  public save(updatedAt: Date) {
    this._updatedAt = updatedAt
    this.filterManager.save()
    this.sortManager.save()
    this.databaseTotalCount = this.totalCount
  }

  public get customizableColumnTemplatesByType(): Map<
    ViewColumnType,
    ViewCustomColumnTemplateBase
  > {
    return this._customizableColumnTemplatesByType
  }

  public get hasCustomizableColumnTemplates(): boolean {
    return !!this.customizableColumnTemplates.length
  }

  public get hasUnsavedChanges(): boolean {
    return this.filterManager.hasUnsavedChanges || this.sortManager.hasUnsavedChanges
  }

  public get updatedAt(): Date {
    return this._updatedAt
  }

  public get protocol(): ViewFragment {
    return {
      columns: toJS(this.columnManager.columns.map((c) => toJS(c.protocol))),
      displayIndex: this.displayIndex,
      filters: toJS(this.filterManager.populatedFilters.map((f) => toJS(f.protocol))),
      entityLayer: this._entityLayer,
      id: this.id,
      name: this.name,
      sorts: toJS(this.sortManager.sorts),
      updatedAt: this._updatedAt.toISOString(),
      viewType: this.viewType,
    }
  }

  public get databaseProtocol(): ViewFragment {
    return {
      entityLayer: this._entityLayer,
      columns: this.columnManager.columns.map((c) => toJS(c.protocol)),
      displayIndex: this.databaseDisplayIndex,
      filters: toJS(
        this.filterManager.filters
          .map((f) => toJS(f.databaseProtocol))
          .filter((f) => TableViewFilter.isFilterPopulated(f))
      ),
      id: this.id,
      name: this.name,
      sorts: toJS(this.sortManager.databaseProtocol),
      updatedAt: this._updatedAt.toISOString(),
      viewType: this.viewType,
    }
  }
}
